export const PERFORMANCE_THRESHOLDS = {
  mer: {
    // < 30 % Critique (rouge) | 30–40 % inclus Bon (vert) | > 40 % Excellent (bleu)
    criticalBelow: 30,
    goodMax: 40
  },
  noPhone: {
    // < 15 % Excellent (vert) | 15–18 % inclus À surveiller (orange) | > 18 % Critique (rouge)
    excellentBelow: 15,
    warningMax: 18
  }
}

export const KPI_COLORS = {
  red: '#ff4d4f',
  orange: '#ff9f1a',
  green: '#57d17c',
  blue: '#4da3ff',
  neutral: '#8b95a7'
}

export function isValidNumber(value) {
  return typeof value === 'number' && Number.isFinite(value)
}

/**
 * Normalize a volume field. Returns null when the value is unusable.
 */
export function sanitizeVolume(value, { allowZero = true } = {}) {
  if (value === null || value === undefined || value === '') {
    return allowZero ? 0 : null
  }
  const num = Number(value)
  if (!Number.isFinite(num) || num < 0) {
    return null
  }
  return num
}

/**
 * Returns null when quotations is 0 / missing, or when inputs are invalid.
 */
export function calculateRate(value, quotations) {
  const safeQuotations = sanitizeVolume(quotations)
  const safeValue = sanitizeVolume(value)

  if (safeQuotations === null || safeValue === null) {
    return null
  }
  if (!safeQuotations || safeQuotations === 0) {
    return null
  }
  if (safeValue > safeQuotations) {
    return null
  }

  const rate = (safeValue / safeQuotations) * 100
  if (!Number.isFinite(rate)) {
    return null
  }
  return rate
}

export function getMerStatus(rate, quotations) {
  const safeQuotations = sanitizeVolume(quotations)

  if (
    safeQuotations === null ||
    !safeQuotations ||
    safeQuotations === 0 ||
    rate === null ||
    !Number.isFinite(rate)
  ) {
    return {
      status: 'no-data',
      label: 'N/A',
      color: 'neutral'
    }
  }

  if (rate < PERFORMANCE_THRESHOLDS.mer.criticalBelow) {
    return {
      status: 'critical',
      label: 'Critique',
      color: 'red'
    }
  }

  if (rate <= PERFORMANCE_THRESHOLDS.mer.goodMax) {
    return {
      status: 'good',
      label: 'Bon',
      color: 'green'
    }
  }

  return {
    status: 'excellent',
    label: 'Excellent',
    color: 'blue'
  }
}

export function getNoPhoneStatus(rate, quotations) {
  const safeQuotations = sanitizeVolume(quotations)

  if (
    safeQuotations === null ||
    !safeQuotations ||
    safeQuotations === 0 ||
    rate === null ||
    !Number.isFinite(rate)
  ) {
    return {
      status: 'no-data',
      label: 'N/A',
      color: 'neutral'
    }
  }

  if (rate < PERFORMANCE_THRESHOLDS.noPhone.excellentBelow) {
    return {
      status: 'excellent',
      label: 'Excellent',
      color: 'green'
    }
  }

  if (rate <= PERFORMANCE_THRESHOLDS.noPhone.warningMax) {
    return {
      status: 'warning',
      label: 'À surveiller',
      color: 'orange'
    }
  }

  return {
    status: 'critical',
    label: 'Critique',
    color: 'red'
  }
}

export function formatRate(rate) {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) {
    return '—'
  }
  return `${rate.toFixed(2).replace('.', ',')} %`
}

export function getColorHex(colorKey) {
  return KPI_COLORS[colorKey] || KPI_COLORS.neutral
}

/**
 * Build a displayable rate cell model for MER or No Phone.
 * Invalid volumes (negative, NaN, value > quotations) yield status "invalid".
 */
export function buildRateModel(value, quotations, type) {
  const safeQuotations = sanitizeVolume(quotations)
  const safeValue = sanitizeVolume(value)

  if (safeQuotations === null || safeValue === null) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[productPerformance] Donnée invalide', { type, value, quotations })
    }
    return {
      rate: null,
      status: 'invalid',
      label: 'Donnée invalide',
      color: 'neutral',
      formatted: '—',
      barWidth: 0
    }
  }

  if (safeValue > safeQuotations && safeQuotations > 0) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[productPerformance] Volume incohérent', { type, value: safeValue, quotations: safeQuotations })
    }
    return {
      rate: null,
      status: 'invalid',
      label: 'Donnée invalide',
      color: 'neutral',
      formatted: '—',
      barWidth: 0
    }
  }

  const rate = calculateRate(safeValue, safeQuotations)
  const statusFn = type === 'noPhone' ? getNoPhoneStatus : getMerStatus
  const status = statusFn(rate, safeQuotations)

  return {
    rate,
    status: status.status,
    label: status.label,
    color: status.color,
    formatted: formatRate(rate),
    barWidth: rate === null ? 0 : Math.min(100, Math.max(0, rate))
  }
}

export function mapProductRow({ name, quotations, mer, abandons, noPhone }) {
  const safeQuotations = sanitizeVolume(quotations)
  const safeMer = sanitizeVolume(mer)
  const safeAbandons = sanitizeVolume(abandons)
  const safeNoPhone = sanitizeVolume(noPhone)

  const quotationsValue = safeQuotations === null ? 0 : safeQuotations
  const merValue = safeMer === null ? 0 : safeMer
  const abandonsValue = safeAbandons === null ? 0 : safeAbandons
  const noPhoneValue = safeNoPhone === null ? 0 : safeNoPhone

  const merModel = buildRateModel(merValue, quotationsValue, 'mer')
  const noPhoneModel = buildRateModel(noPhoneValue, quotationsValue, 'noPhone')

  return {
    product: name,
    nb_cotation: quotationsValue,
    mer: merValue,
    nb_leads_abandonment: abandonsValue,
    nb_leads_nophone: noPhoneValue,
    taux_mer: merModel.rate,
    taux_nophone: noPhoneModel.rate,
    mer_status: merModel,
    nophone_status: noPhoneModel
  }
}

export function calculateTotals(products) {
  const totals = (products || []).reduce(
    (acc, product) => {
      acc.quotations += product.nb_cotation || product.quotations || 0
      acc.mer += product.mer || 0
      acc.noPhone += product.nb_leads_nophone || product.noPhone || 0
      acc.abandons += product.nb_leads_abandonment || product.abandons || 0
      return acc
    },
    {
      quotations: 0,
      mer: 0,
      noPhone: 0,
      abandons: 0
    }
  )

  totals.merRate = calculateRate(totals.mer, totals.quotations)
  totals.noPhoneRate = calculateRate(totals.noPhone, totals.quotations)
  totals.merStatus = getMerStatus(totals.merRate, totals.quotations)
  totals.noPhoneStatus = getNoPhoneStatus(totals.noPhoneRate, totals.quotations)

  return totals
}

/**
 * Alerts ordered by severity:
 * 1. No Phone critical
 * 2. MER critical
 * 3. No Phone warning
 */
export function generateAlerts(products) {
  const noPhoneCritical = []
  const merCritical = []
  const noPhoneWarning = []

  ;(products || []).forEach(product => {
    const name = product.product || product.name
    const quotations = product.nb_cotation || product.quotations || 0
    const mer = product.mer || 0
    const noPhone = product.nb_leads_nophone || product.noPhone || 0

    const merRate = calculateRate(mer, quotations)
    const noPhoneRate = calculateRate(noPhone, quotations)

    if (merRate !== null && merRate < PERFORMANCE_THRESHOLDS.mer.criticalBelow) {
      merCritical.push({
        product: name,
        indicator: 'MER',
        rate: merRate,
        level: 'critical',
        message: 'Taux MER inférieur à 30 %'
      })
    }

    if (noPhoneRate !== null && noPhoneRate > PERFORMANCE_THRESHOLDS.noPhone.warningMax) {
      noPhoneCritical.push({
        product: name,
        indicator: 'NO_PHONE',
        rate: noPhoneRate,
        level: 'critical',
        message: 'Taux No Phone supérieur à 18 %'
      })
    }

    if (
      noPhoneRate !== null &&
      noPhoneRate >= PERFORMANCE_THRESHOLDS.noPhone.excellentBelow &&
      noPhoneRate <= PERFORMANCE_THRESHOLDS.noPhone.warningMax
    ) {
      noPhoneWarning.push({
        product: name,
        indicator: 'NO_PHONE',
        rate: noPhoneRate,
        level: 'warning',
        message: 'Taux No Phone à surveiller'
      })
    }
  })

  return [...noPhoneCritical, ...merCritical, ...noPhoneWarning]
}

/**
 * Sort rates numerically. Rows with no rate (0 cotations / invalid) stay at the
 * bottom for both ascending and descending orders.
 */
export function sortRateNullLast(a, b, prop, order = 'ascending') {
  const va = a[prop]
  const vb = b[prop]
  const aNull = va === null || va === undefined || !Number.isFinite(va)
  const bNull = vb === null || vb === undefined || !Number.isFinite(vb)

  if (aNull && bNull) {
    return 0
  }
  if (aNull) {
    return 1
  }
  if (bNull) {
    return -1
  }

  const cmp = va - vb
  return order === 'descending' ? -cmp : cmp
}

export function formatLastUpdate(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date)
  const pad = n => (n <= 9 ? `0${n}` : `${n}`)
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function resolvePeriodDates(periodKey, today = new Date()) {
  const pad = n => (n <= 9 ? `0${n}` : `${n}`)
  const toIso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  switch (periodKey) {
    case 'yesterday': {
      start.setDate(start.getDate() - 1)
      end.setDate(end.getDate() - 1)
      break
    }
    case 'last7': {
      start.setDate(start.getDate() - 6)
      break
    }
    case 'month': {
      start.setDate(1)
      break
    }
    case 'today':
    default:
      break
  }

  return {
    start: toIso(start),
    end: toIso(end)
  }
}
